/**
 * Conflict detection for the visual schedulers (Scheduling Pro).
 *
 * Two TIMED blocks that cover the same weekday + overlapping time-of-day + overlapping
 * date range are ambiguous: the backend ScheduleResolver picks exactly ONE winner, so
 * the other is "shadowed" (won't play during the overlap). This replicates the resolver's
 * tie-break CLIENT-SIDE (no backend call) so the grid can flag the shadowed block.
 *
 * Tie-break — identical to ScheduleResolver::pick (app/Services/ScheduleResolver.php):
 *   1. higher priority wins
 *   2. then NARROWER time window wins (a specific promo beats a broad menu)
 *   3. then more specific target wins (wall-direct / device over group)
 *   4. then newest (created_at) wins
 *
 * Always-on rows (no time window) are the baseline default and intentionally NOT flagged
 * — a timed block legitimately overrides them only inside its window.
 */

const toSec = (hms) => {
  if (!hms) return null;
  const [h, m, s] = String(hms).split(':');
  return (parseInt(h, 10) * 3600) + (parseInt(m, 10) * 60) + (parseInt(s || 0, 10));
};

// Minute intervals a window occupies on a single day, in [0,86400). Wraps midnight → two.
const intervals = (row) => {
  const s = toSec(row.start_time);
  const e = toSec(row.end_time);
  if (s === null || e === null) return [];
  return e > s ? [[s, e]] : [[s, 86400], [0, e]];
};

const timeOverlap = (a, b) => {
  const ia = intervals(a);
  const ib = intervals(b);
  return ia.some(([s1, e1]) => ib.some(([s2, e2]) => s1 < e2 && s2 < e1));
};

const windowWidth = (row) => {
  const s = toSec(row.start_time);
  const e = toSec(row.end_time);
  if (s === null || e === null) return 86400; // treat untimed as broadest
  return e > s ? e - s : (86400 - s + e);
};

const dayOverlap = (a, b) => {
  const ma = a.days_of_week == null ? 127 : a.days_of_week; // null = every day
  const mb = b.days_of_week == null ? 127 : b.days_of_week;
  return (ma & mb) !== 0;
};

const dateOverlap = (a, b) => {
  const as = a.start_date ? String(a.start_date).slice(0, 10) : null;
  const ae = a.end_date ? String(a.end_date).slice(0, 10) : null;
  const bs = b.start_date ? String(b.start_date).slice(0, 10) : null;
  const be = b.end_date ? String(b.end_date).slice(0, 10) : null;
  if (as && be && as > be) return false; // a starts after b ends
  if (bs && ae && bs > ae) return false; // b starts after a ends
  return true;
};

// Returns the winning row of a pair per the resolver tie-break.
const winnerOf = (a, b, specificityOf) => {
  const pa = a.priority || 0; const pb = b.priority || 0;
  if (pa !== pb) return pa > pb ? a : b;
  const wa = windowWidth(a); const wb = windowWidth(b);
  if (wa !== wb) return wa < wb ? a : b;
  const sa = specificityOf(a); const sb = specificityOf(b);
  if (sa !== sb) return sa < sb ? a : b;
  return String(a.created_at) > String(b.created_at) ? a : b; // newest
};

/**
 * @param {Array} rows                schedule rows (assignments or campaign_schedules)
 * @param {Function} specificityOf    fn(row) → int (0 = more specific). Default 0.
 * @returns {{ shadowed: Set<id>, conflicted: Set<id>, count: number }}
 *          shadowed   = ids that lose at least one overlap (partly overridden)
 *          conflicted = ids involved in any overlap
 *          count      = number of overlapping pairs
 */
export function findOverlaps(rows = [], specificityOf = () => 0) {
  const timed = rows.filter((r) => r.start_time && r.end_time);
  const shadowed = new Set();
  const conflicted = new Set();
  let count = 0;
  for (let i = 0; i < timed.length; i += 1) {
    for (let j = i + 1; j < timed.length; j += 1) {
      const a = timed[i]; const b = timed[j];
      if (!dayOverlap(a, b) || !timeOverlap(a, b) || !dateOverlap(a, b)) continue;
      count += 1;
      conflicted.add(a.id); conflicted.add(b.id);
      const loser = winnerOf(a, b, specificityOf) === a ? b : a;
      shadowed.add(loser.id);
    }
  }
  return { shadowed, conflicted, count };
}
